import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useI18n } from '../i18n/I18nContext';

let youtubeApiPromise;

const loadYouTubeApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (youtubeApiPromise) return youtubeApiPromise;
  youtubeApiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      resolve(window.YT);
    };
    let script = document.getElementById('youtube-iframe-api');
    if (!script) {
      script = document.createElement('script');
      script.id = 'youtube-iframe-api';
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('error', () => reject(new Error('youtube-api-load-failed')), { once: true });
  });
  return youtubeApiPromise;
};

export const COMMUNITY_PLAYER_VARS = { controls: 1, playsinline: 1, rel: 0, disablekb: 1 };

const CommunityYouTubePlayer = forwardRef(({ videoId, onReady, onPlayingChange, onError, onRequestFocus }, ref) => {
  const { t } = useI18n();
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const translationRef = useRef(t);

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  useImperativeHandle(ref, () => ({
    getCurrentTime: () => playerRef.current?.getCurrentTime?.() || 0,
    getDuration: () => playerRef.current?.getDuration?.() || 0,
    isPlaying: () => playerRef.current?.getPlayerState?.() === window.YT?.PlayerState?.PLAYING,
  }));

  useEffect(() => {
    let cancelled = false;
    const restorePageFocus = () => {
      window.setTimeout(() => {
        const iframe = playerRef.current?.getIframe?.();
        if (iframe && document.activeElement === iframe) onRequestFocus?.();
      }, 0);
    };
    window.addEventListener('blur', restorePageFocus);
    loadYouTubeApi().then((YT) => {
      if (cancelled || !hostRef.current) return;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: COMMUNITY_PLAYER_VARS,
        events: {
          onReady: (event) => onReady?.(event.target.getDuration() || 0),
          onStateChange: (event) => {
            const isPlaying = event.data === YT.PlayerState.PLAYING;
            onPlayingChange?.(isPlaying);
            if (isPlaying) window.setTimeout(() => onRequestFocus?.(), 0);
          },
          onError: () => onError?.(translationRef.current('community.error.youtubeUnavailable')),
        },
      });
    }).catch(() => onError?.(translationRef.current('community.error.youtubeConnect')));
    return () => {
      cancelled = true;
      window.removeEventListener('blur', restorePageFocus);
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, [onError, onPlayingChange, onReady, onRequestFocus, videoId]);

  return <div className="community-player-host" ref={hostRef} />;
});

export default CommunityYouTubePlayer;
